import type { Variants } from '@amplitude/experiment-react-native-client';
import { addLoggingContext, removeLoggingContextKeys } from 'features/observability/logger';
import { DD_RUM_FEATURE_FLAG_VALUE_MAX_LENGTH } from 'features/observability/rum/constants';
import {
  normalizeDatadogFeatureFlagKey,
  normalizeDatadogFeatureFlagValue,
} from 'features/observability/rum/datadog-feature-flag-utils';
import {
  removeDatadogRumContext,
  setDatadogRumContext,
} from 'features/observability/rum/datadog-rum-context';

export const EXPERIMENT_VARIANTS_LOG_CONTEXT_KEY = 'experimentVariants';

// Datadog query surfaces:
// - RUM context: experimentVariants.<normalized_experiment_key>
// - Logs: experimentVariants.<normalized_experiment_key>
type ExperimentVariantContext = Record<string, string>;

type DatadogExperimentVariantContext = {
  logContext: ExperimentVariantContext;
  rumContext: ExperimentVariantContext;
};

let previousContextSignature = '';

const stableSerializeContext = (context: ExperimentVariantContext) =>
  JSON.stringify(
    Object.fromEntries(Object.entries(context).sort(([a], [b]) => a.localeCompare(b)))
  );

const truncateExperimentVariantValue = (value: string) =>
  value.length > DD_RUM_FEATURE_FLAG_VALUE_MAX_LENGTH
    ? value.slice(0, DD_RUM_FEATURE_FLAG_VALUE_MAX_LENGTH)
    : value;

export const buildDatadogExperimentVariantContext = (
  variants: Variants
): DatadogExperimentVariantContext => {
  return Object.entries(variants).reduce<DatadogExperimentVariantContext>(
    (acc, [experimentKey, variant]) => {
      const variantValue = variant?.value;

      if (typeof variantValue !== 'string' || variantValue.length === 0) {
        return acc;
      }

      const normalizedExperimentKey = normalizeDatadogFeatureFlagKey(experimentKey);
      const normalizedVariantValue = normalizeDatadogFeatureFlagValue(
        truncateExperimentVariantValue(variantValue)
      ).value;

      if (typeof normalizedVariantValue !== 'string' || normalizedVariantValue.length === 0) {
        return acc;
      }

      acc.logContext[normalizedExperimentKey] = normalizedVariantValue;
      acc.rumContext[normalizedExperimentKey] = normalizedVariantValue;

      return acc;
    },
    { logContext: {}, rumContext: {} }
  );
};

export const syncDatadogExperimentVariantContext = (variants: Variants) => {
  const { logContext, rumContext } = buildDatadogExperimentVariantContext(variants);
  const contextSignature = stableSerializeContext(logContext);

  if (contextSignature === previousContextSignature) {
    return;
  }

  previousContextSignature = contextSignature;

  if (Object.keys(logContext).length > 0) {
    setDatadogRumContext(EXPERIMENT_VARIANTS_LOG_CONTEXT_KEY, rumContext);
    addLoggingContext({
      [EXPERIMENT_VARIANTS_LOG_CONTEXT_KEY]: logContext,
    });
  } else {
    removeDatadogRumContext(EXPERIMENT_VARIANTS_LOG_CONTEXT_KEY);
    removeLoggingContextKeys([EXPERIMENT_VARIANTS_LOG_CONTEXT_KEY]);
  }
};

export const resetDatadogExperimentVariantContextForTests = () => {
  previousContextSignature = '';
};
